import { c as _c } from "react/compiler-runtime";
import React, { Fragment, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import avo from 'analytics/avo';
import PropTypes from 'prop-types';
import useSuspenseQuery from 'commons/useSuspenseQuery';
import IconSwatches from 'components/icons/iconSwatches';
import ClearAllFiltersButton from 'components/consumer/ClearAllFiltersButton';
import FixedWidthContainer from 'components/consumer/FixedWidthContainer';
import SwatchesFabricDetailSidebar from 'components/consumer/fabric-filter/SwatchesFabricDetailSidebar/SwatchesFabricDetailSidebar';
import useFabricFavs from 'data-hooks/useFabricFavs';
import { GET_FABRIC_DATA, GET_FABRIC_GALLERY_DATA } from '@/graphql/queries';
import AdminPlaceholder from 'components/consumer/AdminPlaceholder';
import FabricGridItem from './FabricGridItem';
import { removeEmptyFilters, returnDisplayFabricList } from './utils';
const ZOOM_IMG_PROPS = {
  w: 1800,
  h: 915
};
const JB_IMG_PROPS = {
  height: 300,
  width: 590
};
const IMG_PROPS = {
  auto: 'format,compress',
  fit: 'fill',
  crop: 'entropy',
  h: 430,
  w: 875
};
const URL_PREFIX = 'https://joybird2.imgix.net/fabric-swatches/t1/';
const renderFabricItems = (mobileItemsPerRow, fabricsList, selectedFabric, setSelectedFabric, openSidebarOnClick, favFabricsObj = {}, setOptionAsFav, removeOptionAsFav, toggleFavClick, isUserLoggedIn, desktopFilterSize) => <div className={`relative md:ml-[3.333vw] md:pt-8 grid gap-[2px] ${mobileItemsPerRow === 1 ? 'grid-cols-[repeat(auto-fill,minmax(95%,1fr))]' : mobileItemsPerRow === 2 ? 'grid-cols-[repeat(auto-fill,minmax(45%,1fr))]' : 'grid-cols-[repeat(auto-fill,minmax(120px,1fr))]'} md:gap-8 md:grid-cols-[repeat(auto-fill,minmax(170px,1fr))] flex-1 max-[767px]:w-full`} data-xc="FabricGrid">
    {fabricsList.map((fabricItem, fabricIndex) => {
    const isSelected = !!selectedFabric && !!selectedFabric.footerTitle && !!fabricItem.footerTitle && selectedFabric.footerTitle.name === fabricItem.footerTitle.name;
    const isFavorited = !!favFabricsObj?.[fabricItem.option?.id];
    const {
      alignment,
      footerTitle,
      imageProps,
      isNotHovered,
      title,
      sliderImages
    } = fabricItem;
    return <FabricGridItem {...{
      alignment,
      footerTitle,
      imageProps,
      isNotHovered,
      isSelected,
      title,
      isFavorited,
      setOptionAsFav,
      removeOptionAsFav,
      fabricOption: fabricItem?.option,
      toggleFavClick,
      isUserLoggedIn,
      numOfFavFabrics: favFabricsObj ? Object.keys(favFabricsObj).length : 0
    }} images={sliderImages} mobileItemsPerRow={mobileItemsPerRow} key={footerTitle ? footerTitle.name : fabricIndex} onClick={() => {
      if (typeof setSelectedFabric === 'function') {
        setSelectedFabric(fabricItem);
      }
    }} openSidebarOnClick={openSidebarOnClick} desktopFilterSize={desktopFilterSize} />;
  })}
  </div>;
const renderFabricGridFromList = (mobileItemsPerRow, fabricsList, selectedFabric, setSelectedFabric, sortBy = null, openSidebarOnClick, favFabricsObj = {}, setOptionAsFav, removeOptionAsFav, toggleFavClick, isUserLoggedIn, desktopFilterSize) => {
  if (sortBy && sortBy === 'Family Name') {
    return fabricsList.map(group => <div className="px-0 py-16 max-[767px]:flex-col max-[767px]:pt-0 max-[767px]:px-0 max-[767px]:pb-6 flex [&>div]:flex-1" key={group.groupName}>
        <div className="pt-4 max-w-[600px] mx-[3.333vw] my-0 max-[767px]:mb-6">
          <h1 className="text-3xl font-bold text-gray">{group.groupName}</h1>
          <h4 className="text-base font-bold mt-3 text-gray">
            {group.groupDesc}
          </h4>
        </div>
        <div className={(`px-0 py-16 max-[767px]:flex-col max-[767px]:pt-0 max-[767px]:px-0 max-[767px]:pb-6 ${mobileItemsPerRow === 1 ? 'mobile-one-across' : mobileItemsPerRow === 2 ? 'mobile-two-across' : 'mobile-three-across'}`, {
        '[&>div]:max-[767px]:!grid-cols-[repeat(auto-fill,minmax(95%,1fr))]': mobileItemsPerRow === 1,
        '[&>div]:max-[767px]:!grid-cols-[repeat(auto-fill,minmax(45%,1fr))]': mobileItemsPerRow === 2,
        '[&>div]:max-[767px]:!grid-cols-[repeat(auto-fill,minmax(30%,1fr))]': mobileItemsPerRow === 3
      })}>
          {renderFabricItems(mobileItemsPerRow, group.dataSource, selectedFabric, setSelectedFabric, openSidebarOnClick, favFabricsObj, setOptionAsFav, removeOptionAsFav, toggleFavClick, isUserLoggedIn, desktopFilterSize)}
        </div>
      </div>);
  }
  return renderFabricItems(mobileItemsPerRow, fabricsList, selectedFabric, setSelectedFabric, openSidebarOnClick, favFabricsObj, setOptionAsFav, removeOptionAsFav, toggleFavClick, isUserLoggedIn, desktopFilterSize);
};
const FabricGrid = ({
  config,
  fabricList,
  clearAllFilters,
  mobileItemsPerRow,
  openSidebarOnClick = false,
  sortBy,
  wrapInFixedWidthContainer = true,
  showFavorites = false,
  desktopFilterSize = 0,
  initialSelection = null,
  onFabricSelection = null
}) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedFabricForSidebar, setSelectedFabricForSidebar] = useState(null);
  const [getFabricsFromCache, setGetFabricsFromCache] = useState(true);
  const hasOpenedInitialFabricRef = useRef(false);
  const hasManuallyClosedRef = useRef(false);
  const {
    loading,
    error,
    data
  } = useSuspenseQuery(GET_FABRIC_DATA, {
    variables: {
      filters: config ? {
        ...removeEmptyFilters(config.filters)
      } : {},
      activeOnly: true
    },
    skip: fabricList || config?.dataSource
  });
  const {
    data: fabricsGalleryData
  } = useSuspenseQuery(GET_FABRIC_GALLERY_DATA, {
    variables: {
      fromCache: getFabricsFromCache
    }
  });
  const {
    actions: {
      setOptionAsFav,
      removeOptionAsFav
    } = {},
    data: {
      userFabricFavs: favFabricsObj,
      isUserLoggedIn
    } = {}
  } = useFabricFavs();
  const filterFavorites = useCallback(list => showFavorites && favFabricsObj ? list.filter(fabric => favFabricsObj[fabric.option?.id]) : list, [favFabricsObj, showFavorites]);
  const addFabricGalleryData = useCallback(list_0 => fabricsGalleryData?.getFabricGalleryData ? list_0.map(fabric_0 => {
    const itemData = fabricsGalleryData.getFabricGalleryData[fabric_0.option?.id];
    if (!itemData) return fabric_0;
    let galleryData;
    try {
      galleryData = JSON.parse(itemData);
    } catch (e) {
      galleryData = null;
    }
    if (galleryData && galleryData.img_prefix && galleryData.img_suffixes) {
      const img_suffixesList = galleryData.img_suffixes.length ? galleryData.img_suffixes.split(',') : [];
      const images = img_suffixesList.map(suffix => ({
        url: URL_PREFIX.concat(galleryData.img_prefix, `-t1-${suffix}.jpg`),
        isVideo: false,
        imgProps: IMG_PROPS,
        zoomUrl: URL_PREFIX.concat(galleryData.img_prefix, `-t1-${suffix}.jpg`),
        videoProps: {},
        className: '',
        zoomImgProps: ZOOM_IMG_PROPS,
        jbImgProps: JB_IMG_PROPS
      }));
      return {
        ...fabric_0,
        sliderImages: images
      };
    }
    return fabric_0;
  }) : list_0, [fabricsGalleryData?.getFabricGalleryData]);

  // Compute the fully derived fabric list in a single pass
  const finalFabricList = useMemo(() => {
    const baseList = fabricList || config?.dataSource || [];
    const filteredList = filterFavorites(baseList);
    if (filteredList.length > 0 && Array.isArray(filteredList[0]?.dataSource)) {
      return filteredList.map(group => ({
        ...group,
        dataSource: addFabricGalleryData(group.dataSource)
      }));
    }
    // Apply favorites filter if needed and
    // attach images from gallery data if available
    return addFabricGalleryData(filteredList);
  }, [addFabricGalleryData, config?.dataSource, fabricList, filterFavorites]);

  // Check if any fabrics are missing slider images and trigger a refresh
  useEffect(() => {
    if (fabricsGalleryData?.getFabricGalleryData) {
      const flatList = finalFabricList.length > 0 && Array.isArray(finalFabricList[0]?.dataSource) ? finalFabricList.flatMap(group_0 => group_0.dataSource) : finalFabricList;
      const fabricsMissingSliderImages = flatList.filter(fabric_1 => !fabric_1?.sliderImages) || [];
      if (fabricsMissingSliderImages.length > 0 && getFabricsFromCache) {
        setGetFabricsFromCache(false);
      }
    }
  }, [fabricsGalleryData, finalFabricList, getFabricsFromCache]);
  const GridWrapper = wrapInFixedWidthContainer ? FixedWidthContainer : Fragment;
  const gridWrapperProps = wrapInFixedWidthContainer ? {
    noMobilePadding: true,
    noTabletPadding: true
  } : {};
  const toggleFavClick = useCallback(favParams => {
    if (favFabricsObj?.[favParams?.fabricId] && removeOptionAsFav) {
      removeOptionAsFav(favParams.fabricId);
      avo.fabricUnfavorited(favParams);
    } else if (!favFabricsObj?.[favParams?.fabricId] && setOptionAsFav) {
      setOptionAsFav(favParams.fabricId);
      avo.fabricFavorited(favParams);
    }
  }, [favFabricsObj, removeOptionAsFav, setOptionAsFav]);

  // Handler for fabric click - opens SidebarModal instead of push-across sidebar
  const handleFabricClick = useCallback(fabric_2 => {
    if (onFabricSelection) {
      onFabricSelection(fabric_2);
    }
    hasManuallyClosedRef.current = false; // Reset manual close flag when user clicks a fabric
    setSelectedFabricForSidebar(fabric_2);
    setIsSidebarOpen(true);
  }, [onFabricSelection]);

  // Open sidebar when fabric is restored from URL (on page refresh)
  useEffect(() => {
    if (initialSelection && !hasOpenedInitialFabricRef.current && !hasManuallyClosedRef.current && !selectedFabricForSidebar // Only open if no fabric is currently selected
    ) {
      const initialFabricName = initialSelection?.footerTitle?.name;
      if (initialFabricName) {
        hasOpenedInitialFabricRef.current = true;
        setSelectedFabricForSidebar(initialSelection);
        setIsSidebarOpen(true);
      }
    }
  }, [initialSelection, selectedFabricForSidebar]);

  // Simple grid rendering without push-across sidebar logic
  const renderFabricGridMethod = useCallback((fabricsList = finalFabricList) => renderFabricGridFromList(mobileItemsPerRow, fabricsList, selectedFabricForSidebar,
  // Pass selected fabric for highlighting
  handleFabricClick,
  // Use our handler instead of GridWithSidebar's
  sortBy, openSidebarOnClick, favFabricsObj, setOptionAsFav, removeOptionAsFav, toggleFavClick, isUserLoggedIn, desktopFilterSize), [desktopFilterSize, finalFabricList, favFabricsObj, handleFabricClick, isUserLoggedIn, mobileItemsPerRow, openSidebarOnClick, removeOptionAsFav, setOptionAsFav, sortBy, toggleFavClick, selectedFabricForSidebar]);
  const displayFabricList = useMemo(() => data ? returnDisplayFabricList(data?.getFabrics) : [], [data]);

  // When loaded with new config return dynamic fabric list
  if (!fabricList && config && !config.dataSource) {
    if (error) return null;
    if (loading) {
      // TODO: Change this to a proper placeholder
      return <GridWrapper>Loading ...</GridWrapper>;
    }
    if (!data) return null;
    if (data && data.getFabrics) {
      return <GridWrapper {...gridWrapperProps}>
          <div className="flex flex-col items-start md:flex-row md:mx-[3.333vw] md:my-0 md:pt-8">
            {!!config.heading && !!config.heading.content && <div className="w-full flex flex-col p-8 text-gray md:w-[30%]">
                <div className="text-xl font-bold">
                  {config.heading.content}
                </div>

                {!!config.subHeading && !!config.subHeading.content && <div className="text-sm mt-4">
                    {config.subHeading.content}
                  </div>}

                {!!config.text && !!config.text.content && <div className="text-sm font-bold text-gray">
                    {config.text.content}
                  </div>}
              </div>}
            {renderFabricGridMethod(displayFabricList)}
            {selectedFabricForSidebar && <SwatchesFabricDetailSidebar isOpen={isSidebarOpen} onClose={() => {
            hasManuallyClosedRef.current = true;
            setIsSidebarOpen(false);
            setSelectedFabricForSidebar(null);
          }} fabricId={selectedFabricForSidebar?.option?.id} mobileHideHeader galleryType="swatches" />}
          </div>
        </GridWrapper>;
    }
    return <GridWrapper {...gridWrapperProps}>
        <div className="relative md:ml-[3.333vw] md:pt-8 flex flex-row px-0 py-[60px] ml-[3.333vw]">
          <div className="items-start flex flex-col w-[100px]">
            <IconSwatches className="block h-[50px] w-[75px] [&_g]:stroke-[#f1f1f1]" />
          </div>

          <div className="flex-1">
            <h3 className="text-gray text-xl font-bold">
              Unfortunately, we don&apos;t currently offer a fabric that matches
              your filters.
            </h3>
          </div>
        </div>
      </GridWrapper>;
  }

  // Temporarily adding the dataSource check till it is replaced with dynamic list
  return finalFabricList && finalFabricList.length ? config && config.dataSource ? <GridWrapper {...gridWrapperProps}>
        <div className="w-full">
          <div className={sortBy === 'Family Name' ? 'my-0 ml-auto mr-0 md:mr-[var(--push-across-sidebar-content-right-margin)] md:!mr-[3.333vw]' : 'flex flex-col items-start md:flex-row md:mx-[3.333vw] md:my-0 md:pt-8'}>
            {renderFabricGridMethod(displayFabricList)}
            {selectedFabricForSidebar && <SwatchesFabricDetailSidebar isOpen={isSidebarOpen} onClose={() => {
          hasManuallyClosedRef.current = true;
          setIsSidebarOpen(false);
          setSelectedFabricForSidebar(null);
        }} fabricId={selectedFabricForSidebar?.option?.id} mobileHideHeader galleryType="swatches" />}
          </div>
        </div>
      </GridWrapper> : <GridWrapper {...gridWrapperProps}>
        <div className="w-full">
          <div className={sortBy === 'Family Name' ? 'my-0 ml-auto mr-0 md:mr-[var(--push-across-sidebar-content-right-margin)] md:!mr-[3.333vw]' : 'flex flex-col items-start md:flex-row md:mx-[3.333vw] md:my-0 md:pt-8'}>
            {renderFabricGridMethod(finalFabricList)}
            {selectedFabricForSidebar && <SwatchesFabricDetailSidebar isOpen={isSidebarOpen} onClose={() => {
          hasManuallyClosedRef.current = true;
          setIsSidebarOpen(false);
          setSelectedFabricForSidebar(null);
        }} fabricId={selectedFabricForSidebar?.option?.id} mobileHideHeader galleryType="swatches" />}
          </div>
        </div>
      </GridWrapper> : <GridWrapper {...gridWrapperProps}>
      <div className="relative md:ml-[3.333vw] md:pt-8 flex flex-row px-0 py-[60px] ml-[3.333vw]">
        <div className="items-start flex flex-col w-[100px]">
          <IconSwatches className="block h-[50px] w-[75px] [&_g]:stroke-[#f1f1f1]" />
        </div>

        <div className="flex-1">
          <h3 className="text-gray text-xl font-bold">
            {showFavorites ? "You haven't favorited any fabrics yet." : "Unfortunately, we don't currently offer a fabric that matches your filters."}
          </h3>

          <h4 className="text-gray text-base mt-1.5 mb-7">
            {showFavorites ? 'Double click on fabric swatches to favorite or unfavorite them.' : 'Please try a different combination of filters.'}
          </h4>

          {clearAllFilters && <ClearAllFiltersButton onClick={clearAllFilters} />}
        </div>
      </div>
    </GridWrapper>;
};
FabricGrid.propTypes = {
  clearAllFilters: PropTypes.func,
  config: PropTypes.shape({
    dataSource: PropTypes.string,
    filters: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      options: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string,
        value: PropTypes.string
      }))
    })),
    heading: PropTypes.shape({
      content: PropTypes.string
    }),
    subHeading: PropTypes.shape({
      content: PropTypes.string
    }),
    text: PropTypes.shape({
      content: PropTypes.string
    })
  }),
  fabricList: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    isFavorited: PropTypes.bool,
    name: PropTypes.string,
    swatch: PropTypes.string
  })),
  mobileItemsPerRow: PropTypes.number,
  openSidebarOnClick: PropTypes.func,
  showFavorites: PropTypes.bool,
  sortBy: PropTypes.string,
  wrapInFixedWidthContainer: PropTypes.bool,
  desktopFilterSize: PropTypes.number,
  initialSelection: PropTypes.objectOf(PropTypes.object),
  onFabricSelection: PropTypes.func
};
const FabricGridUI = props => {
  const $ = _c(8);
  if (props?.isAdmin) {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <AdminPlaceholder className="!flex !flex-col !items-start md:!flex-row md:!mx-[3.333vw] md:!my-0 md:!pt-8 !min-h-[596px]">Fabric Grid Component Placeholder for Variant Builder. <br />You can still edit the component config, which will then display correctly in Consumer</AdminPlaceholder>;
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    return t0;
  }
  if (props?.config?.elementAttribute?.id) {
    const t0 = props?.config?.elementAttribute?.id;
    let t1;
    if ($[1] !== props) {
      t1 = <FabricGrid {...props} />;
      $[1] = props;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    let t2;
    if ($[3] !== t0 || $[4] !== t1) {
      t2 = <div id={t0}>{t1}</div>;
      $[3] = t0;
      $[4] = t1;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    return t2;
  }
  let t0;
  if ($[6] !== props) {
    t0 = <FabricGrid {...props} />;
    $[6] = props;
    $[7] = t0;
  } else {
    t0 = $[7];
  }
  return t0;
};
FabricGridUI.propTypes = {
  isAdmin: PropTypes.bool,
  config: PropTypes.shape({
    elementAttribute: PropTypes.shape({
      id: PropTypes.string
    })
  }),
  initialSelection: PropTypes.objectOf(PropTypes.object),
  onFabricSelection: PropTypes.func
};
export default FabricGridUI;